import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Globe, Send, MapPin } from "lucide-react";
import { Reveal, MagneticButton } from "../helpers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

console.log("Backend URL:", process.env.REACT_APP_BACKEND_URL);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SERVICES = ["Web Development", "App Development", "AI Automation", "Digital Consultation", "Other"];

const inputClass =
  "w-full bg-[#0B0B0E] border border-white/10 rounded-xl px-4 py-3.5 font-body text-white placeholder:text-brand-muted focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange focus:shadow-[0_0_20px_rgba(255,138,0,0.15)] transition-[border-color,box-shadow] duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and requirements.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-32 bg-[#0B0B0E] border-t border-white/5">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] blur-[140px] opacity-10 rounded-full"
        style={{ background: "radial-gradient(circle,#FF8A00,transparent 70%)" }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 relative">
        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] text-brand-orange uppercase mb-4">/ Contact</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Let&apos;s build something <span className="text-gradient-orange">remarkable</span>
            </h2>
            <p className="mt-6 font-body text-brand-sec leading-relaxed max-w-md">
              Tell us about your project. We&apos;ll get back to you with a plan to bring it to life.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            {[
              { icon: Phone, label: "+91 78638 19886", href: "tel:+917863819886" },
              { icon: Mail, label: "paramarshtech0@gmail.com", href: "mailto:paramarshtech0@gmail.com" },
              { icon: Globe, label: "paramarshtech.co.in", href: "https://paramarshtech.co.in" },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <a
                  data-testid={`contact-info-${i}`}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl glass border border-white/10 group-hover:border-brand-orange/40 transition-colors">
                    <c.icon className="h-4.5 w-4.5 text-brand-orange" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono text-sm text-brand-sec group-hover:text-white transition-colors">{c.label}</span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { name: "Surat", src: "https://www.google.com/maps?q=21.1980619,72.8297585&z=12&output=embed" },
                { name: "Valsad", src: "https://www.google.com/maps?q=20.6098893,72.930411&z=12&output=embed" },
              ].map((m) => (
                <div key={m.name} className="rounded-2xl overflow-hidden border border-white/10 glass relative group">
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
                    <MapPin className="h-4 w-4 text-brand-orange" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-brand-sec tracking-wide">{m.name}</span>
                  </div>
                  <div className="relative">
                    <iframe
                      title={`Paramarsh Tech - ${m.name}`}
                      src={m.src}
                      className="w-full h-40"
                      style={{ border: 0, filter: "invert(0.92) hue-rotate(185deg) saturate(0.85) brightness(0.95) contrast(0.95)" }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 30%, rgba(255,138,0,0.12), transparent 65%)", mixBlendMode: "overlay" }} />
                    <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 40px rgba(74,240,255,0.08)" }} />
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="h-full">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="gradient-border rounded-3xl glass border border-white/10 p-8 md:p-10 space-y-5 h-full flex flex-col"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="contact-name" className={inputClass} placeholder="Name *" value={form.name} onChange={set("name")} />
              <input data-testid="contact-email" type="email" className={inputClass} placeholder="Email *" value={form.email} onChange={set("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="contact-phone" className={inputClass} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
              <input data-testid="contact-company" className={inputClass} placeholder="Company" value={form.company} onChange={set("company")} />
            </div>
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger data-testid="contact-service" className={inputClass + " h-auto data-[placeholder]:text-brand-muted"}>
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0B0E] border-white/10 text-white">
                {SERVICES.map((s) => (
                  <SelectItem key={s} value={s} className="focus:bg-brand-orange/20 focus:text-white">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <textarea data-testid="contact-message" className={inputClass + " resize-none flex-1 min-h-[120px]"} placeholder="Tell us about your requirements *" value={form.message} onChange={set("message")} />
            <MagneticButton
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-sm font-medium text-black disabled:opacity-60 shadow-[0_0_30px_rgba(255,138,0,0.3)]"
              style={{ background: "linear-gradient(to right,#FF5F00,#FF9D00,#FFC400)" }}
            >
              {loading ? "Sending..." : "Send Message"}
              {!loading && <Send className="h-4 w-4" strokeWidth={2} />}
            </MagneticButton>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
